import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/Toast";
import { useContextMenu } from "../../components/Menu";
import Icon from "../../components/Icon";
import { BackBar, Page } from "../../components/Band";
import { Chip, ClubLogo, Content } from "../../components/primitives";
import ConfirmModal from "../../components/manage/ConfirmModal";
import { useBioPhotos } from "./useBioPhotos";
import { ADMIN_TABS, manageHref } from "./manageUtils";
import OverviewTab from "./OverviewTab";
import EditBioTab from "./EditBioTab";
import EventsTab from "./events/EventsTab";
import MembersTab from "./MembersTab";
import ApplicationsTab from "./ApplicationsTab";
import ShareTab from "./ShareTab";

/**
 * Everything that belongs to ONE club on /manage. ManagePage renders it with key={club.id}, so a club
 * switch (or leaving the page) unmounts the whole photo session, tab state and cached lists, and the
 * next club starts fresh (AdminScreen :823).
 */
export default function ClubManager({ club, clubs, tab, onRefresh, onPatchClub, onTransferred }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const { menu, openMenu } = useContextMenu();
  const [bioSaving, setBioSaving] = useState(false);
  const [bioDirty, setBioDirty] = useState(false);
  const [pendingNav, setPendingNav] = useState(null); // { run } waiting on the discard-changes confirm

  const go = useCallback((clubId, nextTab, state) => navigate(manageHref(clubId, nextTab), state ? { state } : undefined), [navigate]);
  const guarding = tab === "editbio" && bioDirty;
  // Leaving Bio with unsaved edits asks first (tab, club switch, in-app links). BrowserRouter has no
  // useBlocker, so browser Back is not covered; reload / close use EditBioTab's beforeunload prompt.
  const guardedGo = useCallback((clubId, nextTab) => {
    if (guarding && (clubId !== club.id || nextTab !== tab)) setPendingNav({ run: () => go(clubId, nextTab) });
    else go(clubId, nextTab);
  }, [guarding, club.id, tab, go]);
  const discardAndLeave = () => {
    const next = pendingNav;
    setPendingNav(null);
    setBioDirty(false);
    photos.cancel();
    next?.run();
  };

  useEffect(() => {
    if (!guarding) return undefined;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.target.closest?.('[data-testid="manage.back"]')) {
        e.preventDefault();
        e.stopPropagation();
        // Same as BackBar's own back(): history when there is some, else the /clubs fallback.
        setPendingNav({ run: () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/clubs", { replace: true })) });
        return;
      }
      const a = e.target.closest?.("a[href]");
      if (!a || (a.target && a.target !== "_self") || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation(); // capture phase on document: React Router's own Link handler never runs
      setPendingNav({ run: () => navigate(url.pathname + url.search + url.hash) });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [guarding, navigate]);
  const onPatch = useCallback((fields) => onPatchClub(club.id, fields), [club.id, onPatchClub]);
  const notify = useCallback((m, icon) => toast(m, icon), [toast]);
  const photos = useBioPhotos({ uid: user?.uid, clubId: club.id, savedGallery: club.gallery, notify });

  // Overview "Add Photos" tile: pick + upload first, then open Edit Bio at the gallery (AdminScreen :1683).
  const addPhotosFromOverview = useCallback(async () => {
    const saved = Array.isArray(club.gallery) ? club.gallery.filter((u) => typeof u === "string" && u) : [];
    const picked = await photos.pickPhotos(saved);
    if (picked) go(club.id, "editbio", { keepStaged: true });
  }, [club, photos, go]);

  const multi = clubs.length > 1;
  const openSwitcher = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    openMenu(
      Math.max(8, r.right - 260),
      r.bottom + 6,
      clubs.map((c) => ({ icon: c.id === club.id ? "check" : undefined, label: c.name, testId: `manage.switch.${c.id}`, run: () => guardedGo(c.id, tab) }))
    );
  };

  const switcher = (
    <Chip
      glass
      className="mg-switch"
      data-testid="manage.switchClub"
      aria-haspopup={multi ? "menu" : undefined}
      onClick={multi ? openSwitcher : undefined}
      disabled={bioSaving}
      style={multi ? undefined : { cursor: "default" }}
    >
      {/* Named by its text (no aria-label: axe label-content-name-mismatch with the logo initials); on phones
          the name is visually hidden, not display:none, so the chip keeps it. */}
      {multi && <span className="mg-sr">Switch club, current:</span>}
      <ClubLogo club={club} size={26} />
      <span className="mg-switch-name">{club.name}</span>
      {multi && <Icon name="down" size={14} />}
    </Chip>
  );

  const body = {
    overview: <OverviewTab club={club} onTab={(t) => go(club.id, t)} onAddPhotos={addPhotosFromOverview} onPatch={onPatch} />,
    editbio: (
      <EditBioTab
        club={club}
        photos={photos}
        keepStaged={!!location.state?.keepStaged}
        onPatch={onPatch}
        onSavingChange={setBioSaving}
        onDirtyChange={setBioDirty}
        onClose={() => go(club.id, "overview")}
      />
    ),
    events: <EventsTab club={club} onClubChanged={onRefresh} />,
    users: <MembersTab club={club} onTransferred={onTransferred} />,
    applications: <ApplicationsTab club={club} />,
    share: <ShareTab club={club} />,
  }[tab];

  return (
    <Page className="mg-page">
      <BackBar title="Manage Club" right={switcher} testId="manage" />
      <Content>
        <div className="dock" role="tablist" style={{ marginBottom: 20 }}>
          {ADMIN_TABS.map(([k, icon, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} data-testid={`manage.tab.${k}`} disabled={bioSaving} className={tab === k ? "on" : ""} onClick={() => guardedGo(club.id, k)}>
              <Icon name={icon} size={16} />{label}
            </button>
          ))}
        </div>
        {body}
      </Content>
      {menu}
      {pendingNav && (
        <ConfirmModal
          title="Discard changes?"
          confirmLabel="Discard"
          danger
          testId="manage.bio.leaveConfirm"
          onClose={() => setPendingNav(null)}
          onConfirm={discardAndLeave}
        >
          You have unsaved changes to this club&apos;s bio. Leave without saving?
        </ConfirmModal>
      )}
    </Page>
  );
}
